import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, AlertCircle, X } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { Checkbox } from '@/components/ui/checkbox'
import { GlowEffect } from '@/components/ui/GlowEffect'
import { typography, formStyles } from '@/constants/styles'
import SignInBackground from '@/assets/SignIn.png'
import checkCircleIcon from '@/assets/check_circle.svg'
import type { SignupScreenBaseProps } from './SignupScreenBase'
import { getPasswordStrength } from './SignupScreenBase'

export const SignupScreenMobile = ({
  formData,
  showPassword,
  showConfirmPassword,
  acceptTerms,
  loading,
  error,
  onFormDataChange,
  onShowPasswordChange,
  onShowConfirmPasswordChange,
  onAcceptTermsChange,
  onClearField,
  onSubmit,
}: SignupScreenBaseProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({
      [e.target.name]: e.target.value
    })
  }

  const passwordStrength = getPasswordStrength(formData.password)

  return (
    <div className="min-h-screen bg-[#f2f1f6] dark:bg-[linear-gradient(178deg,rgba(27,27,25,1)_1%,rgba(37,35,14,1)_52%,rgba(27,27,25,1)_97%)] relative flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[520px] z-10"
      >
        <div className="text-center mb-8">
          <h1 className={typography.h1}>
            Create Account
          </h1>
          <p className={typography.body}>
            Join SecondLane and start trading
          </p>
        </div>

        <div className={`${formStyles.card} p-6`}>
          {/* Light theme: background image */}
          <div 
            className="absolute inset-0 dark:hidden"
            style={{ 
              backgroundImage: `url(${SignInBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
          {/* Dark theme: green glow effect */}
          <GlowEffect className="hidden dark:block" />
          <div className="relative z-10">
            <form onSubmit={onSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 flex items-center gap-2 rounded">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className={typography.labelBlock}>
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  leftElement={<Mail size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                  rightElement={formData.email ? (
                    <button
                      type="button"
                      onClick={() => onClearField('email')}
                      className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors"
                    >
                      <X size={18} />
                    </button>
                  ) : undefined}
                  className={formStyles.input}
                  required
                />
              </div>

              <div>
                <label className={typography.labelBlock}>
                  Password
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  leftElement={<Lock size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => onShowPasswordChange(!showPassword)}
                      className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  className={formStyles.input}
                  required
                />
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex gap-1">
                        <div 
                          className={`flex-1 h-2.5 rounded transition-all duration-300 ${
                            passwordStrength.level >= 1 
                              ? '' 
                              : 'bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(255,255,255,0.24)]'
                          }`}
                          style={passwordStrength.level >= 1 ? {
                            backgroundColor: passwordStrength.bgColor
                          } : undefined}
                        />
                        <div 
                          className={`flex-1 h-2.5 rounded transition-all duration-300 ${
                            passwordStrength.level >= 2 
                              ? '' 
                              : 'bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(255,255,255,0.24)]'
                          }`}
                          style={passwordStrength.level >= 2 ? {
                            backgroundColor: passwordStrength.bgColor
                          } : undefined}
                        />
                        <div 
                          className={`flex-1 h-2.5 rounded transition-all duration-300 ${
                            passwordStrength.level >= 3 
                              ? '' 
                              : 'bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(255,255,255,0.24)]'
                          }`}
                          style={passwordStrength.level >= 3 ? {
                            backgroundColor: passwordStrength.bgColor
                          } : undefined}
                        />
                        <div 
                          className={`flex-1 h-2.5 rounded transition-all duration-300 ${
                            passwordStrength.level >= 4 
                              ? '' 
                              : 'bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(255,255,255,0.24)]'
                          }`}
                          style={passwordStrength.level >= 4 ? {
                            backgroundColor: passwordStrength.bgColor
                          } : undefined}
                        />
                      </div>
                      {passwordStrength.text && (
                        <span 
                          className="font-plus-jakarta font-semibold text-[12px] leading-[16px] dark:text-white"
                          style={{ color: passwordStrength.color }}
                        >
                          {passwordStrength.text}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={typography.labelBlock}>
                  Confirm Password
                </label>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  leftElement={<Lock size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => onShowConfirmPasswordChange(!showConfirmPassword)}
                      className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  className={formStyles.input}
                  required
                />
                {formData.confirmPassword && formData.password && (
                  <div className="mt-2 flex items-center gap-2">
                    {formData.password === formData.confirmPassword ? (
                      <>
                        <img src={checkCircleIcon} alt="" className="w-4 h-4" />
                        <span className="font-plus-jakarta font-semibold text-[12px] leading-[16px] text-[#3d8c05]">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} className="text-red-500" />
                        <span className="font-plus-jakarta font-semibold text-[12px] leading-[16px] text-red-500">Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-terms-mobile"
                  checked={acceptTerms}
                  onCheckedChange={(checked) => onAcceptTermsChange(checked as boolean)}
                  className={formStyles.checkbox + " mt-1"}
                />
                <label 
                  htmlFor="accept-terms-mobile"
                  className={typography.secondaryText + " cursor-pointer"}
                >
                  I agree to the{' '}
                  <Link to="/terms" className={typography.link}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className={typography.link}>
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full h-12 bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(255,255,255,0.72)] hover:bg-[rgba(45,41,35,0.85)] dark:hover:bg-[rgba(255,255,255,0.85)] text-white dark:text-[#2d2923] hover:text-white dark:hover:text-[#2d2923] font-plus-jakarta font-medium text-[14px] leading-[20px] rounded-lg transition-all duration-200"
                isLoading={loading}
              >
                CREATE ACCOUNT
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className={typography.secondaryText + " flex flex-col items-center justify-center gap-2"}>
                <span>Already have an account?</span>
                <Link
                  to="/login"
                  className={typography.link}
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

